import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { ValidationFlag } from "@/components/invoice/ValidationFlag";
import { Plus, Trash2, RefreshCw, CheckCircle2 } from "lucide-react";
import { formatINR } from "@/lib/format";

const DEPARTMENTS = ["Civil", "Mechanical", "Electrical"];

const Field = ({ label, value, onChange, mono, readOnly, type = "text", testId }) => (
  <div>
    <label className="text-xs font-mono uppercase tracking-[0.08em] text-[#5A6B63] font-semibold">{label}</label>
    <Input
      data-testid={testId}
      value={value ?? ""}
      onChange={(e) => onChange(type === "number" ? e.target.valueAsNumber : e.target.value)}
      type={type}
      readOnly={readOnly}
      className={`mt-1.5 h-10 rounded-md border-[#DCE3DF] focus-visible:ring-2 focus-visible:ring-[#0E6E52]/20 focus-visible:border-[#0E6E52] ${
        mono ? "font-mono" : ""
      } ${readOnly ? "bg-[#EEF1EF] text-[#5A6B63]" : "bg-white"}`}
    />
  </div>
);

export const ReviewForm = ({
  extraction,
  setExtraction,
  validations,
  department,
  setDepartment,
  onRevalidate,
  isRevalidating,
  onBook,
  isBooking,
  hasWarning,
  hasError,
  status,
  bookedBy,
  readOnly,
}) => {
  if (!extraction) return null;

  const update = (field) => (val) => setExtraction((prev) => ({ ...prev, [field]: val }));
  const updateLineItem = (idx, field) => (val) =>
    setExtraction((prev) => {
      const items = [...(prev.line_items || [])];
      items[idx] = { ...items[idx], [field]: val };
      return { ...prev, line_items: items };
    });
  const addLineItem = () =>
    setExtraction((prev) => ({
      ...prev,
      line_items: [...(prev.line_items || []), { description: "", hsn_sac: "", quantity: 0, unit: "", rate: 0, amount: 0 }],
    }));
  const removeLineItem = (idx) =>
    setExtraction((prev) => ({ ...prev, line_items: prev.line_items.filter((_, i) => i !== idx) }));

  return (
    <div
      className="bg-white border border-[#DCE3DF] rounded-[10px] shadow-[0_4px_20px_rgba(20,30,26,0.06)] p-6 space-y-6"
      data-testid="review-form"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-display font-medium text-[#141E1A]">Review &amp; Validate</h3>
        {!readOnly && (
          <button
            data-testid="revalidate-button"
            onClick={onRevalidate}
            disabled={isRevalidating}
            className="flex items-center gap-1.5 text-sm text-[#0E6E52] hover:text-[#1D3730] disabled:opacity-40 transition-colors duration-200"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isRevalidating ? "animate-spin" : ""}`} /> Re-check
          </button>
        )}
      </div>

      <div className="space-y-2" data-testid="validation-flags-list">
        {validations?.map((v, i) => (
          <ValidationFlag key={i} {...v} />
        ))}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Invoice Number" value={extraction.invoice_number} onChange={update("invoice_number")} mono readOnly={readOnly} testId="field-invoice-number" />
        <Field label="Invoice Date" value={extraction.invoice_date} onChange={update("invoice_date")} mono readOnly={readOnly} testId="field-invoice-date" />
        <Field label="Vendor Name" value={extraction.vendor_name} onChange={update("vendor_name")} readOnly={readOnly} testId="field-vendor-name" />
        <Field label="Vendor GSTIN" value={extraction.vendor_gstin} onChange={update("vendor_gstin")} mono readOnly={readOnly} testId="field-vendor-gstin" />
        <Field label="PO Reference" value={extraction.po_reference} onChange={update("po_reference")} mono readOnly={readOnly} testId="field-po-reference" />
        <Field label="Place of Supply" value={extraction.place_of_supply} onChange={update("place_of_supply")} readOnly={readOnly} testId="field-place-of-supply" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="text-xs font-mono uppercase tracking-[0.08em] text-[#5A6B63] font-semibold">Line Items</label>
          {!readOnly && (
            <button
              data-testid="add-line-item-button"
              onClick={addLineItem}
              className="flex items-center gap-1 text-xs text-[#0E6E52] hover:text-[#1D3730] transition-colors duration-200"
            >
              <Plus className="w-3.5 h-3.5" /> Add row
            </button>
          )}
        </div>
        <div className="border border-[#DCE3DF] rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-[#DCE3DF]">
                <TableHead>Description</TableHead>
                <TableHead>HSN/SAC</TableHead>
                <TableHead>Qty</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Amount</TableHead>
                {!readOnly && <TableHead></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(extraction.line_items || []).map((li, idx) => (
                <TableRow key={idx} className="border-[#DCE3DF]" data-testid={`line-item-row-${idx}`}>
                  <TableCell>
                    <Input readOnly={readOnly} value={li.description ?? ""} onChange={(e) => updateLineItem(idx, "description")(e.target.value)} className="h-8 text-sm border-[#DCE3DF]" />
                  </TableCell>
                  <TableCell>
                    <Input readOnly={readOnly} value={li.hsn_sac ?? ""} onChange={(e) => updateLineItem(idx, "hsn_sac")(e.target.value)} className="h-8 text-sm font-mono border-[#DCE3DF] w-24" />
                  </TableCell>
                  <TableCell>
                    <Input readOnly={readOnly} type="number" value={li.quantity ?? ""} onChange={(e) => updateLineItem(idx, "quantity")(e.target.valueAsNumber)} className="h-8 text-sm font-mono border-[#DCE3DF] w-20" />
                  </TableCell>
                  <TableCell>
                    <Input readOnly={readOnly} value={li.unit ?? ""} onChange={(e) => updateLineItem(idx, "unit")(e.target.value)} className="h-8 text-sm border-[#DCE3DF] w-16" />
                  </TableCell>
                  <TableCell>
                    <Input readOnly={readOnly} type="number" value={li.rate ?? ""} onChange={(e) => updateLineItem(idx, "rate")(e.target.valueAsNumber)} className="h-8 text-sm font-mono border-[#DCE3DF] w-24" />
                  </TableCell>
                  <TableCell className="font-mono text-sm">{formatINR(li.amount)}</TableCell>
                  {!readOnly && (
                    <TableCell>
                      <button data-testid={`remove-line-item-${idx}`} onClick={() => removeLineItem(idx)}>
                        <Trash2 className="w-3.5 h-3.5 text-[#B23A3A]" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Field label="Taxable Value" value={extraction.taxable_value} onChange={update("taxable_value")} type="number" mono readOnly={readOnly} testId="field-taxable-value" />
        <Field label="IGST" value={extraction.igst} onChange={update("igst")} type="number" mono readOnly={readOnly} testId="field-igst" />
        <Field label="CGST" value={extraction.cgst} onChange={update("cgst")} type="number" mono readOnly={readOnly} testId="field-cgst" />
        <Field label="SGST" value={extraction.sgst} onChange={update("sgst")} type="number" mono readOnly={readOnly} testId="field-sgst" />
        <Field label="Total Tax" value={extraction.total_tax} onChange={update("total_tax")} type="number" mono readOnly={readOnly} testId="field-total-tax" />
        <Field label="Grand Total" value={extraction.grand_total} onChange={update("grand_total")} type="number" mono readOnly={readOnly} testId="field-grand-total" />
      </div>
      <p className="text-right text-lg font-mono font-medium text-[#141E1A]" data-testid="grand-total-display">
        Grand Total: {formatINR(extraction.grand_total)}
      </p>

      <div className="pt-4 border-t border-[#DCE3DF] space-y-4">
        {!readOnly ? (
          <>
            <div>
              <label className="text-xs font-mono uppercase tracking-[0.08em] text-[#5A6B63] font-semibold">Booking Department</label>
              <Select value={department} onValueChange={setDepartment}>
                <SelectTrigger data-testid="department-select" className="mt-1.5 h-10 border-[#DCE3DF] w-60">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENTS.map((d) => (
                    <SelectItem key={d} value={d} data-testid={`department-option-${d.toLowerCase()}`}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {hasError && (
              <p className="text-sm text-[#B23A3A] font-medium" data-testid="booking-blocked-message">
                Resolve the red validation error(s) above before booking.
              </p>
            )}
            <div className="flex gap-3">
              <Button
                data-testid="book-to-erp-button"
                disabled={hasError || !department || isBooking}
                onClick={() => onBook(hasWarning)}
                className="bg-[#0E6E52] hover:bg-[#1D3730] text-white rounded-md px-6 h-10 transition-colors duration-200 disabled:opacity-40"
              >
                {isBooking ? "Booking…" : hasWarning ? "Submit anyway" : "Approve & book"}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex items-center gap-2 text-sm font-medium" data-testid="booking-result-banner">
            <CheckCircle2 className={`w-4 h-4 ${status === "booked" ? "text-[#0E6E52]" : "text-[#B7791A]"}`} />
            <span className={status === "booked" ? "text-[#0E6E52]" : "text-[#B7791A]"}>
              {status === "booked" ? "Booked to ERP" : "Submitted — flagged for review"} · {bookedBy}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
